import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  H2,
  Image,
  Input,
  Paragraph,
  ScrollView,
  Spinner,
  Text,
  XStack,
  YStack,
} from 'tamagui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Screen } from '@mrrmlab/ui';
import type { FuzzyMatchSuggestion } from '@mrrmlab/api-client';
import { useApiClient } from '../api-client-context';
import { shoppingListKeys } from '../query-keys';
import { toCreateListItemDto } from '../adapters';
import type { ShoppingListWithItems } from '../types';

export interface ReviewItem {
  name: string;
  suggestions?: FuzzyMatchSuggestion[];
  cropUri?: string | null;
}

interface ReviewEntry {
  id: string;
  name: string;
  selected: boolean;
  suggestion: FuzzyMatchSuggestion | null;
  cropUri: string | null;
}

export interface ReviewScreenProps {
  listId: string;
  initialItems: ReviewItem[];
  previewImage?: string | null;
  onBack?: () => void;
  onImported?: () => void;
}

export function ReviewScreen({ listId, initialItems, previewImage, onBack, onImported }: ReviewScreenProps) {
  const client = useApiClient();
  const qc = useQueryClient();
  const nextId = useRef(0);
  const makeId = () => String(nextId.current++);

  const [entries, setEntries] = useState<ReviewEntry[]>(() =>
    initialItems.map((item) => ({
      id: makeId(),
      name: item.name,
      selected: true,
      suggestion: pickTopSuggestion(item),
      cropUri: item.cropUri ?? null,
    })),
  );
  const [newName, setNewName] = useState('');
  const [importedCount, setImportedCount] = useState<number | null>(null);

  // Auto-navigate after success banner is shown
  useEffect(() => {
    if (importedCount === null) return;
    const timer = setTimeout(() => onImported?.(), 1500);
    return () => clearTimeout(timer);
  }, [importedCount, onImported]);

  const bulkAdd = useMutation({
    mutationFn: async (names: string[]) => {
      // Deduplicate within the selection (case-insensitive)
      const seen = new Set<string>();
      const deduped: string[] = [];
      for (const name of names) {
        const key = name.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          deduped.push(name);
        }
      }

      // Skip names already present in the list
      const cached = qc.getQueryData<ShoppingListWithItems>(shoppingListKeys.detail(listId));
      const existingNames = new Set(
        (cached?.items ?? []).map((i) => i.title.toLowerCase().trim()),
      );
      const toAdd = deduped.filter((name) => !existingNames.has(name.toLowerCase()));

      for (const name of toAdd) {
        await client.lists.items.add(listId, toCreateListItemDto({ title: name, quantity: 1 }));
      }
      return toAdd.length;
    },
    onSuccess: (count) => {
      qc.invalidateQueries({ queryKey: shoppingListKeys.detail(listId) });
      setImportedCount(count);
    },
  });

  const toggle = (id: string) =>
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, selected: !e.selected } : e)));

  const rename = (id: string, name: string) =>
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, name } : e)));

  const remove = (id: string) => setEntries((prev) => prev.filter((e) => e.id !== id));

  const applySuggestion = (id: string) =>
    setEntries((prev) =>
      prev.map((e) =>
        e.id === id && e.suggestion ? { ...e, name: e.suggestion.candidate, suggestion: null } : e,
      ),
    );

  const dismissSuggestion = (id: string) =>
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, suggestion: null } : e)));

  const addEntry = () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    setEntries((prev) => [
      ...prev,
      { id: makeId(), name: trimmed, selected: true, suggestion: null, cropUri: null },
    ]);
    setNewName('');
  };

  const selectedNames = entries
    .filter((e) => e.selected && e.name.trim())
    .map((e) => e.name.trim());

  const handleConfirm = () => bulkAdd.mutate(selectedNames);

  return (
    <Screen>
      <XStack alignItems="center" gap="$3">
        {onBack ? (
          <Button size="$2" chromeless onPress={onBack} disabled={bulkAdd.isPending}>
            ← Back
          </Button>
        ) : null}
        <H2 flex={1}>Erkannte Artikel</H2>
      </XStack>

      <ScrollView flex={1} contentContainerStyle={{ gap: 16, paddingBottom: 16 }}>
        {previewImage ? (
          <Image
            source={{ uri: `data:image/jpeg;base64,${previewImage}` }}
            width="100%"
            height={180}
            resizeMode="contain"
            borderRadius="$3"
            backgroundColor="$color2"
          />
        ) : null}

        {importedCount !== null ? (
          <Paragraph color="$green10" fontWeight="600">
            ✓ {importedCount === 0
              ? 'Keine neuen Artikel (bereits vorhanden)'
              : `${importedCount} Artikel hinzugefügt`}
          </Paragraph>
        ) : null}

        {bulkAdd.isError ? (
          <Paragraph color="$red10" fontSize="$3">
            Fehler beim Import: {bulkAdd.error.message}
          </Paragraph>
        ) : null}

        {entries.length === 0 ? (
          <Paragraph opacity={0.7}>Keine Artikel erkannt.</Paragraph>
        ) : (
          <YStack gap="$2">
            {entries.map((entry) => (
              <YStack key={entry.id} gap="$1">
                {entry.cropUri ? (
                  <Image
                    source={{ uri: entry.cropUri }}
                    width="100%"
                    height={48}
                    resizeMode="contain"
                    borderRadius="$2"
                    backgroundColor="$color2"
                  />
                ) : null}
                <XStack alignItems="center" gap="$2">
                  <Checkbox
                    id={`review-${entry.id}`}
                    size="$4"
                    checked={entry.selected}
                    onCheckedChange={() => toggle(entry.id)}
                    disabled={bulkAdd.isPending || importedCount !== null}
                  >
                    <Checkbox.Indicator>
                      <Text fontSize="$3" fontWeight="700">✓</Text>
                    </Checkbox.Indicator>
                  </Checkbox>
                  <Input
                    flex={1}
                    value={entry.name}
                    onChangeText={(name) => rename(entry.id, name)}
                    opacity={entry.selected ? 1 : 0.4}
                    editable={!bulkAdd.isPending && importedCount === null}
                  />
                  <Button
                    size="$2"
                    chromeless
                    disabled={bulkAdd.isPending || importedCount !== null}
                    onPress={() => remove(entry.id)}
                  >
                    ✕
                  </Button>
                </XStack>
                {entry.suggestion ? (
                  <XStack gap="$2" alignItems="center" paddingLeft="$6">
                    <Paragraph fontSize="$2" opacity={0.7}>
                      Meintest du
                    </Paragraph>
                    <Button
                      size="$2"
                      theme="blue"
                      disabled={bulkAdd.isPending || importedCount !== null}
                      onPress={() => applySuggestion(entry.id)}
                    >
                      {entry.suggestion.candidate}
                    </Button>
                    <Button
                      size="$2"
                      chromeless
                      disabled={bulkAdd.isPending || importedCount !== null}
                      onPress={() => dismissSuggestion(entry.id)}
                    >
                      ✕
                    </Button>
                  </XStack>
                ) : null}
              </YStack>
            ))}
          </YStack>
        )}

        <XStack gap="$2" alignItems="center">
          <Input
            flex={1}
            placeholder="Artikel hinzufügen…"
            value={newName}
            onChangeText={setNewName}
            onSubmitEditing={addEntry}
            editable={!bulkAdd.isPending && importedCount === null}
          />
          <Button
            size="$2"
            onPress={addEntry}
            disabled={bulkAdd.isPending || importedCount !== null || !newName.trim()}
          >
            +
          </Button>
        </XStack>
      </ScrollView>

      <Button
        disabled={bulkAdd.isPending || importedCount !== null || selectedNames.length === 0}
        onPress={handleConfirm}
        theme="active"
      >
        {bulkAdd.isPending ? (
          <Spinner size="small" />
        ) : (
          `${selectedNames.length} Artikel importieren`
        )}
      </Button>
    </Screen>
  );
}

function pickTopSuggestion(item: ReviewItem): FuzzyMatchSuggestion | null {
  const top = item.suggestions?.[0];
  if (!top) return null;
  if (top.candidate.trim().toLowerCase() === item.name.trim().toLowerCase()) return null;
  return top;
}
